// utils/TimeSliceGenerator.ts

import { TimeSliceViewType, TimeSliceWindow } from '../models/TimeSlice';

export class TimeSliceGenerator {
  /**
   * Generates time slice data (start, end, increment) based on a view type and a reference date.
   *
   * @param viewType The desired time slice view ('7d', '24h', '2h').
   * @param referenceDateTime Optional. The central Date object for which to generate the slice. Defaults to current time (new Date()).
   * @param roundToNearestUpperInterval Optional. If true, rounds the `endTime` up to the nearest interval boundary. Defaults to false.
   * @returns An object containing startTime, endTime (ISO 8601 strings), and incrementMinutes.
   */
  public generateTimeSlice(
    viewType: TimeSliceViewType,
    referenceDateTime: Date = new Date(), // Default to current time
    roundToNearestUpperInterval: boolean = false // New optional flag
  ): TimeSliceWindow {
    let startTime: Date;
    let endTime: Date;
    let incrementMinutes: number;

    // Ensure we're working with a mutable copy of the reference date to avoid side effects
    const workingRefTime = new Date(referenceDateTime.getTime());

    switch (viewType) {
      case '7d':
        incrementMinutes = 1440; // 24 hours * 60 minutes

        // For '7d', the natural upper bound is the end of the day
        endTime = new Date(
          workingRefTime.getFullYear(),
          workingRefTime.getMonth(),
          workingRefTime.getDate(),
          23, 59, 59, 999
        );
        // Start is 6 days before the END of the reference day to cover 7 full days
        startTime = new Date(endTime.getTime());
        startTime.setDate(startTime.getDate() - 6);
        startTime.setHours(0, 0, 0, 0); // Ensure it starts at the very beginning of that day
        break;

      case '24h':
        incrementMinutes = 180; // 3 hours * 60 minutes

        if (roundToNearestUpperInterval) {
          // Calculate minutes since epoch
          const totalMinutes = Math.floor(workingRefTime.getTime() / (1000 * 60));
          // Round up to the next 3-hour (180-minute) boundary
          const roundedMinutes = Math.ceil(totalMinutes / incrementMinutes) * incrementMinutes;
          endTime = new Date(roundedMinutes * 1000 * 60);
        } else {
          endTime = workingRefTime;
        }
        // Start is 24 hours before the (potentially rounded) end time
        startTime = new Date(endTime.getTime() - 24 * 60 * 60 * 1000);
        break;

      case '2h':
        incrementMinutes = 15; // 15 minutes

        if (roundToNearestUpperInterval) {
          // Calculate minutes since epoch
          const totalMinutes = Math.floor(workingRefTime.getTime() / (1000 * 60));
          // Round up to the next 15-minute boundary
          const roundedMinutes = Math.ceil(totalMinutes / incrementMinutes) * incrementMinutes;
          endTime = new Date(roundedMinutes * 1000 * 60);
        } else {
          endTime = workingRefTime;
        }
        // Start is 2 hours before the (potentially rounded) end time
        startTime = new Date(endTime.getTime() - 2 * 60 * 60 * 1000);
        break;

      default:
        // This case should ideally not be reached due to TypeScript's type checking,
        // but it's good for robustness if viewType comes from an external source.
        throw new Error(`Unsupported TimeSliceViewType: ${viewType}`);
    }

    return {
      startTime: startTime,
      startTimeStr: startTime.toISOString(), // Convert to ISO 8601 / RFC 3339
      endTime: endTime,
      endTimeStr: endTime.toISOString(),     // Convert to ISO 8601 / RFC 3339
      incrementMinutes: incrementMinutes,
    };
  }

  /**
   * Generates time options to help with picking a time slot.
   *
   * @param viewType The desired time slice view ('7d', '24h', '2h').
   * @param intervalMinutes Optional. interval between different timeslots.
   * @returns An array of time slots staring at 00:00 and in increments of intervalMinutes
   */
  public generateTimeOptions( viewType: TimeSliceViewType, intervalMinutes: number = 15): string[] {
    const options: string[] = [];
    const totalMinutesInDay = 24 * 60;

    for (let m = 0; m < totalMinutesInDay; m += intervalMinutes) {
      const hours = Math.floor(m / 60);
      const minutes = m % 60;

      const formattedHours = String(hours).padStart(2, '0');
      const formattedMinutes = String(minutes).padStart(2, '0');

      options.push(`${formattedHours}:${formattedMinutes}`);
    }
    
    return options;
  };  
}